import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError, formatSAR } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { StatusBadge } from "../DisputesPages";
import {
  MessageSquareWarning, RefreshCw, Loader2, ShieldCheck,
  AlertTriangle, Send, Paperclip, Lock, CheckCircle2, XCircle,
  ArrowUpCircle, Gavel, Hand, Clock, ExternalLink, FileText, Eye,
} from "lucide-react";

/* ─── Top-level admin tab ────────────────────────────────────────────── */
export default function DisputesTab() {
  const { lang } = useI18n();
  const [statusFilter, setStatusFilter] = useState("open_all");
  const [data, setData] = useState({ items: [], counts: {} });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);

  const reload = async () => {
    setBusy(true); setErr(null);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await apiClient.get("/admin/disputes", { params });
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, [statusFilter]);

  const TABS = [
    { id: "open_all", ar: "كل المفتوحة", en: "All open" },
    { id: "open", ar: "بانتظار رد", en: "Pending response" },
    { id: "under_review", ar: "تحت المراجعة", en: "Under review" },
    { id: "resolution_proposed", ar: "اقتراح حل", en: "Proposed" },
    { id: "escalated", ar: "تصعيد", en: "Escalated" },
    { id: "resolved", ar: "محلولة", en: "Resolved" },
    { id: "closed", ar: "مغلقة", en: "Closed" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-disputes-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[#0a0a0a] inline-flex items-center gap-2">
            <MessageSquareWarning className="w-6 h-6 text-[#8a6f2e]" />
            {lang === "ar" ? "مركز النزاعات" : "Dispute Center"}
            <span className="text-sm font-normal text-[#8a6f2e] bg-[#fafaf7] border border-[#c9a55c]/40 rounded-full px-2 py-0.5">
              {data.counts.open_all || 0} {lang === "ar" ? "مفتوحة" : "open"}
            </span>
          </h2>
          <p className="text-sm text-[#6b6b6b] mt-1">
            {lang === "ar" ? "إدارة النزاعات: مراجعة، وساطة، اقتراح حل، إغلاق. لا يمكن حذف النزاعات (سجل قانوني)." : "Manage disputes: review, mediate, propose, close. Disputes cannot be deleted (legal audit)."}
          </p>
        </div>
        <button onClick={reload} disabled={busy} className="btn-ghost text-sm" data-testid="admin-disputes-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {lang === "ar" ? "تحديث" : "Refresh"}
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="admin-disputes-filters">
        {TABS.map((tb) => {
          const c = data.counts?.[tb.id] ?? data.counts?.[tb.id === "open" ? "pending_first_response" : tb.id] ?? 0;
          return (
            <button key={tb.id} onClick={() => setStatusFilter(tb.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${statusFilter === tb.id ? "bg-[#0a0a0a] border-[#0a0a0a] text-[#c9a55c]" : "bg-white border-[#0a0a0a14] text-[#3a3a3a] hover:border-[#c9a55c]"}`}
              data-testid={`admin-dispute-tab-${tb.id}`}
            >
              {lang === "ar" ? tb.ar : tb.en} ({c})
            </button>
          );
        })}
      </div>

      {err && <div className="rounded-xl p-3 text-sm bg-red-50 border border-red-200 text-red-700">{String(err)}</div>}

      {/* List */}
      <div className="space-y-3">
        {busy && data.items.length === 0 ? (
          <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : data.items.length === 0 ? (
          <div className="surface rounded-2xl p-12 text-center text-[#6b6b6b]" data-testid="admin-disputes-empty">
            {lang === "ar" ? "لا توجد نزاعات في هذه الحالة." : "No disputes in this state."}
          </div>
        ) : data.items.map((d) => (
          <div key={d.id} className="surface rounded-2xl p-5" data-testid={`admin-dispute-row-${d.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-2 flex-wrap">
                  <code className="text-xs font-mono text-[#8a6f2e]">{d.code}</code>
                  <StatusBadge status={d.status} lang={lang} />
                  {d.priority && d.priority !== "normal" && (
                    <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5"
                      style={{ backgroundColor: d.priority === "urgent" ? "#b91c1c20" : "#8a6f2e20",
                               color: d.priority === "urgent" ? "#b91c1c" : "#8a6f2e" }}
                    >{d.priority}</span>
                  )}
                </div>
                <h3 className="font-display text-base text-[#0a0a0a] mt-1 truncate">{d.subject}</h3>
                <div className="text-xs text-[#6b6b6b] mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span><strong>{lang === "ar" ? "المشتري:" : "Buyer:"}</strong> {d.buyer?.name || d.buyer_id?.slice(0, 8) || "—"}</span>
                  <span><strong>{lang === "ar" ? "البائع:" : "Seller:"}</strong> {d.seller?.name || d.seller_id?.slice(0, 8) || "—"}</span>
                  {d.amount_disputed_sar && <span><strong>{lang === "ar" ? "المبلغ:" : "Amount:"}</strong> {formatSAR(d.amount_disputed_sar)} {lang === "ar" ? "ريال" : "SAR"}</span>}
                  <span>{new Date(d.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB")}</span>
                </div>
              </div>
              <button onClick={() => setOpenId(d.id)} className="btn-primary text-xs" data-testid={`admin-dispute-open-${d.id}`}>
                <Eye className="w-3.5 h-3.5" /> {lang === "ar" ? "افتح" : "Open"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {openId && (
        <DisputeDetailModal id={openId} onClose={() => setOpenId(null)} onChange={reload} />
      )}
    </div>
  );
}

/* ─── Detail modal: full admin actions ─────────────────────────────── */
function DisputeDetailModal({ id, onClose, onChange }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [action, setAction] = useState(null); // "internal_note" | "request" | "propose" | "escalate" | "decide" | "close"
  const [acting, setActing] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.get(`/admin/disputes/${id}`);
      setData(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [id]);

  const close = () => onClose();

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto"
      style={{ backgroundColor: "rgba(10,10,10,0.75)" }} onClick={close} data-testid="admin-dispute-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full my-8 shadow-2xl">
        {busy ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : err ? (
          <div className="p-6"><div className="rounded-xl p-4 bg-red-50 border border-red-200 text-red-700">{String(err)}</div></div>
        ) : data && (
          <div className="p-6">
            <Header d={data.dispute} lang={lang} onClose={close} />
            <PartiesRow d={data.dispute} lang={lang} />
            {data.dispute.resolution_offer && <OfferCard offer={data.dispute.resolution_offer} lang={lang} />}
            {data.dispute.final_decision && <DecisionCard decision={data.dispute.final_decision} lang={lang} />}

            {/* Action toolbar */}
            <div className="my-5 flex flex-wrap gap-2" data-testid="admin-dispute-actions">
              {!data.dispute.assigned_admin_id && (
                <ActionBtn icon={Hand} onClick={async () => { setActing(true); await apiClient.post(`/admin/disputes/${id}/assign`); await load(); onChange(); setActing(false); }} testid="admin-dispute-assign">
                  {lang === "ar" ? "خصصها لي" : "Assign to me"}
                </ActionBtn>
              )}
              <ActionBtn icon={FileText} onClick={() => setAction("request")} testid="admin-dispute-request-evidence">
                {lang === "ar" ? "اطلب أدلة" : "Request evidence"}
              </ActionBtn>
              <ActionBtn icon={ShieldCheck} variant="primary" onClick={() => setAction("propose")} testid="admin-dispute-propose">
                {lang === "ar" ? "اقترح حلاً" : "Propose resolution"}
              </ActionBtn>
              <ActionBtn icon={ArrowUpCircle} variant="warn" onClick={() => setAction("escalate")} testid="admin-dispute-escalate">
                {lang === "ar" ? "تصعيد" : "Escalate"}
              </ActionBtn>
              {user?.role === "super_admin" && ["escalated", "mediating", "resolution_proposed"].includes(data.dispute.status) && (
                <ActionBtn icon={Gavel} variant="primary" onClick={() => setAction("decide")} testid="admin-dispute-decide">
                  {lang === "ar" ? "قرار نهائي" : "Final decision"}
                </ActionBtn>
              )}
              <ActionBtn icon={XCircle} variant="ghost" onClick={() => setAction("close")} testid="admin-dispute-close">
                {lang === "ar" ? "إغلاق" : "Close"}
              </ActionBtn>
              <ActionBtn icon={Lock} variant="ghost" onClick={() => setAction("internal_note")} testid="admin-dispute-internal">
                {lang === "ar" ? "ملاحظة داخلية" : "Internal note"}
              </ActionBtn>
            </div>

            {/* Action panel */}
            {action && <ActionPanel id={id} action={action} onDone={() => { setAction(null); load(); onChange(); }} onCancel={() => setAction(null)} lang={lang} />}

            {/* Messages thread */}
            <h3 className="font-display text-base mt-6 mb-3">{lang === "ar" ? "سجل المحادثة" : "Conversation"}</h3>
            <div className="space-y-2 mb-4">
              {data.messages.map((m) => (
                <AdminMessage key={m.id} m={m} lang={lang} disputeId={id} />
              ))}
            </div>

            {/* Audit log */}
            <details className="mt-4">
              <summary className="text-xs font-semibold text-[#8a6f2e] cursor-pointer">{lang === "ar" ? "سجل التدقيق" : "Audit log"} ({data.audit?.length || 0})</summary>
              <div className="mt-2 space-y-1 text-[11px] text-[#525252] font-mono bg-[#fafaf7] rounded-xl p-3 max-h-60 overflow-auto">
                {(data.audit || []).map((a) => (
                  <div key={a.id}>
                    [{new Date(a.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}] {a.actor_role}:{a.actor_name} → <strong>{a.action}</strong>
                    {a.from_status && a.to_status && ` (${a.from_status} → ${a.to_status})`}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

function Header({ d, lang, onClose }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3 pb-4 border-b border-[#0a0a0a]/[0.08]">
      <div>
        <code className="text-xs font-mono text-[#8a6f2e]">{d.code}</code>
        <h3 className="font-display text-xl text-[#0a0a0a] mt-1">{d.subject}</h3>
        <div className="mt-1.5 inline-flex items-center gap-2 flex-wrap">
          <StatusBadge status={d.status} lang={lang} />
          {d.amount_disputed_sar && <span className="text-xs text-[#3a3a3a]">{formatSAR(d.amount_disputed_sar)} {lang === "ar" ? "ريال" : "SAR"}</span>}
        </div>
      </div>
      <button onClick={onClose} className="text-[#6b6b6b] hover:text-[#0a0a0a]"><XCircle className="w-5 h-5" /></button>
    </div>
  );
}

function PartiesRow({ d, lang }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3 text-xs">
      <PartyCard label={lang === "ar" ? "المشتري" : "Buyer"} u={d.buyer} role="buyer" lang={lang} />
      <PartyCard label={lang === "ar" ? "البائع" : "Seller"} u={d.seller} role="seller" lang={lang} />
      <div className="rounded-xl bg-[#fafaf7] border border-[#0a0a0a]/[0.08] p-3">
        <div className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">{lang === "ar" ? "السياق" : "Context"}</div>
        {d.vehicle_id && <div className="mt-1.5 text-xs"><Link to={`/vehicles/${d.vehicle_id}`} className="text-[#1f4f8a] hover:underline inline-flex items-center gap-1">{lang === "ar" ? "السيارة" : "Vehicle"} <ExternalLink className="w-3 h-3" /></Link></div>}
        {d.conversation_id && <div className="mt-1.5 text-xs">{lang === "ar" ? "محادثة:" : "Convo:"} <code className="text-[10px]">{d.conversation_id.slice(0, 8)}</code></div>}
        {d.escrow_payment_id && <div className="mt-1.5 text-xs"><Lock className="w-3 h-3 inline text-[#8a6f2e]" /> {lang === "ar" ? "Escrow:" : "Escrow:"} <code className="text-[10px]">{d.escrow_payment_id.slice(0, 8)}</code></div>}
      </div>
    </div>
  );
}

function PartyCard({ label, u, lang }) {
  if (!u) return <div className="rounded-xl bg-[#fafaf7] border border-[#0a0a0a]/[0.08] p-3 text-[#a8a8a8]">{label}: —</div>;
  const verified = u.verified && u.kyc_status === "approved";
  return (
    <div className="rounded-xl bg-[#fafaf7] border border-[#0a0a0a]/[0.08] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#8a8a8a]">{label}</div>
      <div className="text-sm font-semibold text-[#0a0a0a] mt-1 truncate">{u.name || u.email}</div>
      <div className="text-[10px] text-[#6b6b6b]">{u.email}</div>
      {verified ? (
        <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 mt-1.5 bg-[#0a4f3a]/10 text-[#0a4f3a]"><ShieldCheck className="w-3 h-3" /> {lang === "ar" ? "موثّق" : "Verified"}</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 mt-1.5 bg-[#fafaf7] text-[#8a6f2e] border border-[#c9a55c]/40"><AlertTriangle className="w-3 h-3" /> {lang === "ar" ? "غير موثّق" : "Unverified"}</span>
      )}
    </div>
  );
}

function OfferCard({ offer, lang }) {
  return (
    <div className="mt-4 rounded-xl p-3 border-2" style={{ backgroundColor: "#0a4f3a0d", borderColor: "#0a4f3a40" }}>
      <div className="text-xs font-bold text-[#0a4f3a]">{lang === "ar" ? "اقتراح حل نشط" : "Active offer"}</div>
      <div className="text-xs text-[#3a3a3a] mt-1">
        {offer.type} {offer.amount_sar ? `· ${formatSAR(offer.amount_sar)} SAR` : ""}
        {" — "}{lang === "ar" ? "ردود:" : "Responses:"} buyer={offer.buyer_response}, seller={offer.seller_response}
      </div>
    </div>
  );
}

function DecisionCard({ decision, lang }) {
  return (
    <div className="mt-4 rounded-xl p-3 border-2" style={{ backgroundColor: "#fafaf7", borderColor: "rgba(201,165,92,0.40)" }}>
      <div className="text-xs font-bold text-[#8a6f2e]">{lang === "ar" ? "قرار نهائي" : "Final decision"}</div>
      <div className="text-xs text-[#3a3a3a] mt-1">{decision.outcome} {decision.amount_sar ? `· ${formatSAR(decision.amount_sar)} SAR` : ""}</div>
      <p className="text-xs text-[#525252] mt-1">{decision.rationale}</p>
      {decision.action_taken && decision.action_taken !== "none" && (
        <code className="text-[10px] text-[#0a4f3a] block mt-1">→ {decision.action_taken}</code>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, onClick, children, variant = "ghost", testid }) {
  const styles = {
    primary: "btn-primary",
    warn: "btn-ghost text-[#8a6f2e] border-[#c9a55c]/50 hover:bg-[#fafaf7]",
    ghost: "btn-ghost",
  }[variant];
  return <button onClick={onClick} className={`${styles} text-xs`} data-testid={testid}><Icon className="w-3.5 h-3.5" /> {children}</button>;
}

function ActionPanel({ id, action, onDone, onCancel, lang }) {
  const [body, setBody] = useState("");
  const [rationale, setRationale] = useState("");
  const [type, setType] = useState("full_refund");
  const [amount, setAmount] = useState("");
  const [outcome, setOutcome] = useState("buyer_favored");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (action === "internal_note") {
        await apiClient.post(`/disputes/${id}/messages`, { body, is_internal: true, attachments: [] });
      } else if (action === "request") {
        await apiClient.post(`/admin/disputes/${id}/request-evidence`, { body, attachments: [] });
      } else if (action === "propose") {
        await apiClient.post(`/admin/disputes/${id}/propose`, {
          type, amount_sar: amount ? Number(amount) : null, rationale, response_deadline_hours: 48,
        });
      } else if (action === "escalate") {
        await apiClient.post(`/admin/disputes/${id}/escalate`, { rationale });
      } else if (action === "decide") {
        await apiClient.post(`/admin/disputes/${id}/decide`, {
          outcome, rationale, amount_sar: amount ? Number(amount) : null,
        });
      } else if (action === "close") {
        await apiClient.post(`/admin/disputes/${id}/close`, { rationale });
      }
      onDone();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const labels = {
    internal_note: lang === "ar" ? "ملاحظة داخلية (لا تظهر للأطراف)" : "Internal note (hidden from parties)",
    request: lang === "ar" ? "طلب أدلة" : "Request evidence",
    propose: lang === "ar" ? "اقتراح حل" : "Propose resolution",
    escalate: lang === "ar" ? "تصعيد لـ super admin" : "Escalate",
    decide: lang === "ar" ? "قرار نهائي مُلزِم" : "Final binding decision",
    close: lang === "ar" ? "إغلاق النزاع" : "Close dispute",
  };

  return (
    <div className="mt-3 rounded-2xl p-4 border-2" style={{ backgroundColor: "#fafaf7", borderColor: "rgba(201,165,92,0.40)" }} data-testid={`admin-action-${action}`}>
      <h4 className="font-display text-sm text-[#0a0a0a] mb-3">{labels[action]}</h4>
      {err && <div className="mb-3 rounded p-2 text-xs bg-red-50 border border-red-200 text-red-700">{String(err)}</div>}

      {action === "propose" && (
        <>
          <Field label={lang === "ar" ? "نوع الحل" : "Resolution type"}>
            <select className="input-dark" value={type} onChange={(e) => setType(e.target.value)} data-testid="action-offer-type">
              <option value="full_refund">{lang === "ar" ? "استرداد كامل" : "Full refund"}</option>
              <option value="partial_refund">{lang === "ar" ? "استرداد جزئي" : "Partial refund"}</option>
              <option value="replacement">{lang === "ar" ? "استبدال" : "Replacement"}</option>
              <option value="other">{lang === "ar" ? "أخرى" : "Other"}</option>
            </select>
          </Field>
          <Field label={lang === "ar" ? "المبلغ (ريال، اختياري)" : "Amount (SAR, optional)"}>
            <input className="input-dark" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="action-amount" />
          </Field>
          <Field label={lang === "ar" ? "المبرر (يظهر للأطراف)" : "Rationale (visible to parties)"}>
            <textarea rows={3} className="input-dark" value={rationale} onChange={(e) => setRationale(e.target.value)} required data-testid="action-rationale" />
          </Field>
        </>
      )}

      {action === "decide" && (
        <>
          <Field label={lang === "ar" ? "النتيجة" : "Outcome"}>
            <select className="input-dark" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="action-outcome">
              <option value="buyer_favored">{lang === "ar" ? "لصالح المشتري (استرداد)" : "Buyer favored (refund)"}</option>
              <option value="seller_favored">{lang === "ar" ? "لصالح البائع (إفراج)" : "Seller favored (release)"}</option>
              <option value="split">{lang === "ar" ? "تقسيم (استرداد جزئي)" : "Split (partial refund)"}</option>
            </select>
          </Field>
          {outcome === "split" && (
            <Field label={lang === "ar" ? "مبلغ الاسترداد للمشتري" : "Refund amount to buyer"}>
              <input className="input-dark" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="action-amount" />
            </Field>
          )}
          <Field label={lang === "ar" ? "تبرير القرار" : "Decision rationale"}>
            <textarea rows={4} className="input-dark" value={rationale} onChange={(e) => setRationale(e.target.value)} required data-testid="action-rationale" />
          </Field>
        </>
      )}

      {["escalate", "close"].includes(action) && (
        <Field label={lang === "ar" ? "المبرر" : "Rationale"}>
          <textarea rows={3} className="input-dark" value={rationale} onChange={(e) => setRationale(e.target.value)} required data-testid="action-rationale" />
        </Field>
      )}

      {["internal_note", "request"].includes(action) && (
        <Field label={lang === "ar" ? "النص" : "Message"}>
          <textarea rows={3} className="input-dark" value={body} onChange={(e) => setBody(e.target.value)} required data-testid="action-body" />
        </Field>
      )}

      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onCancel} className="btn-ghost text-xs">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
        <button onClick={submit} disabled={busy} className="btn-primary text-xs" data-testid="action-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          {lang === "ar" ? "تأكيد" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="block text-[10px] font-bold uppercase tracking-widest text-[#8a8a8a] mb-1">{label}</span>
      {children}
    </label>
  );
}

function AdminMessage({ m, lang, disputeId }) {
  const isInternal = m.is_internal;
  const isAdmin = m.author_role === "admin" || m.author_role === "super_admin";
  return (
    <div className={`rounded-xl p-3 text-sm ${isInternal ? "bg-[#fef3c7] border border-[#f59e0b]/40" : isAdmin ? "bg-[#1f4f8a]/10 border border-[#1f4f8a]/30" : "bg-white border border-[#0a0a0a]/8"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#525252] mb-1 flex items-center gap-1">
        {isInternal && <Lock className="w-3 h-3" />}
        <strong>{m.author_name || m.author_role}</strong> · {m.author_role} · {new Date(m.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
      </div>
      <p className="whitespace-pre-wrap text-[#0a0a0a]">{m.body}</p>
      {m.attachments?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {m.attachments.map((url, i) => {
            const path = (url || "").replace(/^.*\/api\/files\//, "");
            const proxied = `${process.env.REACT_APP_BACKEND_URL}/api/disputes/${disputeId}/attachments/${path}`;
            return (
              <a key={url || i} href={proxied} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] bg-white/90 border border-[#c9a55c]/40 rounded-md px-2 py-0.5 text-[#3a3a3a]"
              >
                <Paperclip className="w-3 h-3 text-[#8a6f2e]" /> {lang === "ar" ? `دليل ${i + 1}` : `Evidence ${i + 1}`}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
